/**
 * LeverageCapacityWidget
 * Widget para mostrar capacidade de alavancagem financeira no Dashboard
 */

'use client';

import { TrendingUp, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { formatCurrency } from '../../../lib/utils/format';

interface LeverageCapacityWidgetProps {
  totalLimit: number;
  totalUsed: number;
  totalAvailable: number;
  accountsCount: number;
  loading?: boolean;
  onViewDetails?: () => void;
}

export function LeverageCapacityWidget({
  totalLimit,
  totalUsed,
  totalAvailable,
  accountsCount,
  loading = false,
  onViewDetails,
}: LeverageCapacityWidgetProps) {
  if (loading) {
    return <LeverageCapacityWidgetSkeleton />;
  }

  if (accountsCount === 0) {
    return null; // Não mostrar se não há contas de financiamento
  }

  const usagePercent = totalLimit > 0 ? (totalUsed / totalLimit) * 100 : 0;
  const isHighUsage = usagePercent > 80;

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Capacidade de Alavancagem
          </CardTitle>
          {isHighUsage && (
            <AlertCircle className="w-4 h-4 text-warning-600" />
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Limite de financiamento disponível para investimentos
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Indicadores principais */}
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Limite Total</p>
            <p className="text-lg font-bold">{formatCurrency(totalLimit, 'EUR')}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Utilizado</p>
            <p className="text-lg font-bold text-muted-foreground">
              {formatCurrency(totalUsed, 'EUR')}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Disponível</p>
            <p className="text-lg font-bold text-success-600">
              {formatCurrency(totalAvailable, 'EUR')}
            </p>
          </div>
        </div>

        {/* Barra de Progresso */}
        <div className="space-y-2">
          <div className="flex justify-between items-center text-xs">
            <span className="text-muted-foreground">
              {accountsCount} {accountsCount === 1 ? 'conta' : 'contas'}
            </span>
            <span className="font-medium">
              {usagePercent.toFixed(1)}% utilizado
            </span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                isHighUsage ? 'bg-warning-600' : 'bg-primary'
              }`}
              style={{ width: `${Math.min(usagePercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Mensagem de alerta se uso alto */}
        {isHighUsage && (
          <div className="p-2 rounded-md bg-warning-50 border border-warning-200">
            <p className="text-xs text-warning-800">
              <strong>Atenção:</strong> Você está utilizando mais de 80% da sua capacidade de
              financiamento.
            </p>
          </div>
        )}

        {/* Ação */}
        {onViewDetails && (
          <Button
            variant="outline"
            className="w-full"
            size="sm"
            onClick={onViewDetails}
          >
            Ver Detalhes
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function LeverageCapacityWidgetSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-3 w-64 mt-1" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-6 w-20" />
            </div>
          ))}
        </div>
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-9 w-full" />
      </CardContent>
    </Card>
  );
}

